/**
 * Arthaus Card — the ONE client-side product card renderer.
 * Phase: 10 (HD-P0-19)
 *
 * Mirrors snippets/product-card.liquid (same structure + classes) so cards injected by JS
 * (discovery facets / concept chips / load-more / search page) are indistinguishable from
 * server-rendered ones. Change the Liquid card → change this to match (and vice versa).
 *
 * window.ArthausCard.render(product, opts) → HTML string ('' when no handle/url)
 *   product: { handle, title, url?, artist?, image?, mockup_hero?, hero_image?,
 *              image_width?, image_height?, price_formatted?, why?: [string] }
 *   opts.mlt        — include the "More like this" graph-walk launcher (default false)
 *   opts.hideArtist — omit the artist line
 *   opts.priceSlot  — render an empty [data-price-slot] for client-side price hydration
 *   opts.soldBadge  — render a hidden [data-sold-badge] the hydrator can reveal
 *   opts.why        — render the "why" reason chips from product.why (search)
 *   opts.className / opts.style — extra wrapper class/style (e.g. search's concierge-reveal)
 *   opts.eager      — loading="eager" instead of lazy
 *   opts.variant    — 'wall-label' for the museum-label layout (product.size_range → mono placard)
 *   product.mockup_scene (or room_image) — optional in-room scene for the "In a room" grid view
 * The wrapper carries [data-card] so hydrators can find a card's parts via closest().
 */
(function () {
  'use strict';
  if (window.ArthausCard) return; // loaded by several sections — first copy wins

  var SIZES = '(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw';
  var WIDTHS = [300, 450, 600, 900];

  function esc(s) {
    return (s == null ? '' : String(s)).replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // Shopify CDN images resize via ?width= — same srcset the Liquid card emits.
  function isShopifyCdn(url) {
    return /(^|\/\/)cdn\.shopify\.com\//.test(url) || url.indexOf('/cdn/shop/') !== -1;
  }
  function withWidth(url, w) {
    var base = url.replace(/([?&])width=\d+&?/, '$1').replace(/[?&]$/, '');
    return base + (base.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }

  function imageHTML(p, opts) {
    var src = p.mockup_hero || p.hero_image || p.image;
    if (!src) return '';
    src = String(src);
    var w = p.image_width || 450, h = p.image_height || 600;
    var attrs = 'alt="' + esc(p.title) + '" ' +
      'class="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700 ease-out" ' +
      'loading="' + (opts.eager ? 'eager' : 'lazy') + '" width="' + w + '" height="' + h + '"';
    if (isShopifyCdn(src)) {
      var srcset = WIDTHS.map(function (x) { return withWidth(src, x) + ' ' + x + 'w'; }).join(', ');
      return '<img src="' + esc(withWidth(src, 600)) + '" srcset="' + esc(srcset) + '" sizes="' + SIZES + '" ' + attrs + '>';
    }
    return '<img src="' + esc(src) + '" ' + attrs + '>';
  }

  // "In a room" view (assets/hd-room-view.js): the scene is exposed as data and only
  // downloaded when a shopper switches the grid. Payloads without a scene keep the artwork.
  function roomAttrs(p) {
    var scene = p.mockup_scene || p.room_image;
    if (!scene) return '';
    scene = String(scene);
    var a = ' data-hd-room="' + esc(isShopifyCdn(scene) ? withWidth(scene, 600) : scene) + '"';
    if (isShopifyCdn(scene)) {
      a += ' data-hd-room-srcset="' + esc(WIDTHS.map(function (x) { return withWidth(scene, x) + ' ' + x + 'w'; }).join(', ')) + '"';
    }
    return a;
  }

  // Museum wall label (opts.variant === 'wall-label'): mirrors the Liquid card's variant.
  function wallLabelHTML(p, opts, price) {
    return '<div class="text-left">' +
      '<h3 class="font-display italic text-lg lg:text-xl leading-snug tracking-tight text-charcoal">' + esc(p.title) + '</h3>' +
      ((p.artist && !opts.hideArtist) ? '<p class="font-body text-sm text-warm-gray mt-0.5 font-light">' + esc(p.artist) + '</p>' : '') +
      (p.size_range ? '<p class="t-placard text-mute mt-2">' + esc(p.size_range) + '</p>' : '') +
      (price ? '<div class="mt-1.5">' + price + '</div>' : '') +
    '</div>';
  }

  function whyHTML(why) {
    var items = (why || []).slice(0, 2), html = '';
    for (var i = 0; i < items.length; i++) {
      var w = String(items[i]);
      var isDna = /echoes your style/i.test(w);
      html += '<span class="inline-block font-mono text-[10px] tracking-wide lowercase ' +
        (isDna ? 'text-bronze border-bronze/40' : 'text-warm-gray border-charcoal/10') +
        ' border rounded-full px-2 py-0.5 mr-1.5 mt-1.5">' + esc(isDna ? '✦ ' + w.replace(/echoes your style:\s*/i, '') : w) + '</span>';
    }
    return html ? '<div class="leading-none">' + html + '</div>' : '';
  }

  function mltHTML(p) {
    return '<button type="button" ' +
      'class="discovery-mlt absolute top-2.5 right-2.5 z-10 inline-flex items-center gap-1.5 rounded-full bg-white/90 backdrop-blur px-3 py-1.5 font-body text-xs text-charcoal shadow-sm opacity-100 lg:opacity-0 lg:group-hover:opacity-100 focus-visible:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-bronze transition-opacity duration-300 hover:bg-white" ' +
      'data-mlt-handle="' + esc(p.handle) + '" data-mlt-title="' + esc(p.title) + '" aria-label="Explore more like ' + esc(p.title) + '">' +
      '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"></circle><path d="m15.5 8.5-2 5-5 2 2-5z"></path></svg>' +
      '<span class="hidden lg:inline">More like this</span></button>';
  }

  function render(p, opts) {
    opts = opts || {};
    if (!p) return '';
    var url = p.url || (p.handle ? '/products/' + p.handle : '');
    if (!url) return '';

    var price = '';
    if (opts.priceSlot) {
      price = '<span data-price-slot data-handle="' + esc(p.handle) + '" class="font-body text-sm text-charcoal font-light min-h-[1.25rem]"></span>';
    } else if (p.price_formatted) {
      price = '<span class="font-body text-sm text-charcoal">' + esc(p.price_formatted) + '</span>';
    }

    var handle = p.handle || (url.match(/\/products\/([^/?#]+)/) || [])[1] || '';
    var wall = opts.variant === 'wall-label';
    var meta = wall ? wallLabelHTML(p, opts, price) : (
        '<h3 class="font-display text-lg lg:text-xl tracking-tight text-charcoal font-light">' + esc(p.title) + '</h3>' +
        ((p.artist && !opts.hideArtist) ? '<p class="font-body text-sm text-warm-gray mt-1 font-light">' + esc(p.artist) + '</p>' : '') +
        (opts.why ? whyHTML(p.why) : '') +
        (price ?
          '<div class="flex justify-between items-end mt-3 lg:mt-4 border-t border-border/50 pt-3 lg:pt-4">' + price +
            '<span class="font-body text-sm text-bronze opacity-0 group-hover:opacity-100 transition-all duration-300 -translate-x-2 group-hover:translate-x-0 transform font-light">View →</span>' +
          '</div>' : ''));
    return '<div class="group relative' + (opts.className ? ' ' + esc(opts.className) : '') + '" data-card' +
        (handle ? ' data-handle="' + esc(handle) + '"' : '') +
        roomAttrs(p) +
        (opts.style ? ' style="' + esc(opts.style) + '"' : '') + '>' +
      '<a href="' + esc(url) + '" class="block" aria-label="' + esc(p.title) + '">' +
        '<div class="aspect-[3/4] rounded-lg overflow-hidden bg-parchment shadow-sm mb-4 lg:mb-5 relative" data-card-media>' +
          imageHTML(p, opts) +
          (wall ? '' : '<div class="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>') +
          (opts.soldBadge ? '<span data-sold-badge hidden class="absolute top-3 left-3 bg-charcoal/80 text-white font-mono text-[10px] tracking-wide uppercase px-2.5 py-1 rounded-full">Sold</span>' : '') +
        '</div>' +
        meta +
      '</a>' +
      (opts.mlt && p.handle ? mltHTML(p) : '') +
    '</div>';
  }

  window.ArthausCard = { render: render, esc: esc };
})();
